import $ from "jquery";
import i18n, { syncMomentLocale } from "@/i18n";
import moment from "moment";
import "jquery-ui/ui/widgets/datepicker";

let datepickerStylesPromise = null;

const ensureDatepickerStyles = () => {
  if (!datepickerStylesPromise) {
    datepickerStylesPromise = Promise.all([
      import("../../assets/css/jquery-ui.css"),
      import("../../assets/css/calendar.css"),
    ]);
  }
  return datepickerStylesPromise;
};

// Map app locale codes (per i18n APP_LOCALE_CODES) to jQuery UI regional codes.
// English uses the empty-string entry (`$.datepicker.regional[""]`) shipped by jquery-ui.
const JQUI_LOCALE_MAP = {
  en: "",
  de: "de",
  pt: "pt",
  tr: "tr",
  ar: "ar",
  es: "es",
  fr: "fr",
  it: "it",
  nl: "nl",
  pl: "pl",
  ro: "ro",
  ru: "ru",
  sl: "sl",
  zh: "zh-CN",
  da: "da",
  bg: "bg",
};

const localeLoadPromises = {};

const getActiveLocale = () => {
  if (i18n && typeof i18n.locale === "string" && i18n.locale) return i18n.locale;
  if (typeof window !== "undefined") {
    const stored = window.localStorage?.getItem?.("locale");
    if (stored) return stored;
  }
  return "en";
};

const loadDatepickerLocale = (code) => {
  const jquiCode = JQUI_LOCALE_MAP[code] ?? "";
  if (!jquiCode) {
    return Promise.resolve($.datepicker.regional[""] || {});
  }
  if (!localeLoadPromises[jquiCode]) {
    localeLoadPromises[jquiCode] = import(
      /* webpackChunkName: "jqui-i18n-[request]" */
      `jquery-ui/ui/i18n/datepicker-${jquiCode}.js`
    )
      .then(() => $.datepicker.regional[jquiCode] || {})
      .catch(() => $.datepicker.regional[""] || {});
  }
  return localeLoadPromises[jquiCode];
};

class DateRangePicker {
  constructor(fromSelector, toSelector, options = {}) {
    this.minLos = [];
    this.format = options.format || "dd M yy";
    this.toDate = null;
    this.options = options;
    this.fromDate = null;
    this.currency = options.currency || "EUR";
    this.toDateObj = null;
    this.priceData = null;
    this.toSelector = toSelector;
    this.fromDateObj = null;
    this.fromSelector = fromSelector;
    this.numberFormat = options.numberFormat || [];
    this.smartCalendar = options.smartCalendar ?? true;
    this.currencySymbol = options.currencySymbol || "";
    this.presetHighlight = true;
    this.showCalendarPrice = options.showCalendarPrice ?? false;
    this.showImmediately = options.showImmediately ?? false;

    // Load jQuery UI styles only when datepicker functionality is actually used.
    ensureDatepickerStyles();
    this.ready = this._initWithLocale(fromSelector, toSelector);
  }

  async _initWithLocale(fromSelector, toSelector) {
    const code = getActiveLocale();
    // Both jQuery UI's regional pack and moment's locale must be loaded before we can
    // build the merged regional config. jQuery UI's regional pack provides UI labels
    // (prevText, nextText, ...). Moment provides the month/weekday name arrays we use
    // for both popup chrome AND `dateFormat` formatting, so that the displayed input
    // value (e.g. "12 März 2026") parses cleanly through `moment(val, "DD MMM YYYY")`.
    const [jquiRegional] = await Promise.all([
      loadDatepickerLocale(code),
      syncMomentLocale(code),
    ]);
    const localeData = moment.localeData();
    this.regional = {
      ...jquiRegional,
      monthNames: localeData.months(),
      monthNamesShort: localeData.monthsShort(),
      dayNames: localeData.weekdays(),
      dayNamesShort: localeData.weekdaysShort(),
      dayNamesMin: localeData.weekdaysMin(),
      firstDay: 1,
    };
    this.init(fromSelector, toSelector);
  }

  highlightDates(date) {
    const day = moment(date).format("YYYY-MM-DD");
    const los = this.minLos[day] || 1;
    const displayDate = los === "closed" ? false : true;
    if (this.fromDateObj && date.getTime() === this.fromDateObj.getTime()) {
      return [displayDate, "from-highlight"];
    }
    if (this.toDateObj && date.getTime() === this.toDateObj.getTime()) {
      return [displayDate, "to-highlight"];
    }
    if (!this.fromDateObj || !this.toDateObj) return [displayDate, ""];
    return date >= this.fromDateObj && date <= this.toDateObj
      ? [displayDate, "range-highlight"]
      : [displayDate, ""];
  }

  setPrices(showPrice) {
    if (this.smartCalendar) {
      const self = this;
      $("#ui-datepicker-div td a").removeClass("show-price hide-price");
      $("#ui-datepicker-div td a").addClass(
        showPrice ? "show-price" : "hide-price"
      );
      if (self.priceData) {
        let maxDigits = 0;
        $("#ui-datepicker-div td").each(function () {
          const cellDate = self.getCellDateInstance($(this));
          if (cellDate) {
            const dateString = cellDate.toLocaleDateString("en-CA");
            if (dateString in self.priceData) {
              let price = self.formatPrice(dateString);
              const digits = self.countDigits(price);
              maxDigits = maxDigits > digits ? maxDigits : digits;
              $(this)
                .find("a")
                .attr("data-content", `${self.currencySymbol}${price}`);
            }
          }
        });
        $("#ui-datepicker-div td a").removeClass("price-15 price-14 price-12");
        if (maxDigits > 4) {
          $("#ui-datepicker-div td a").addClass("price-12");
        } else if (maxDigits > 2) {
          $("#ui-datepicker-div td a").addClass("price-14");
        } else {
          $("#ui-datepicker-div td a").addClass("price-15");
        }
      }
    }
  }

  formatPrice(dateString) {
    const self = this;
    let price = self.priceData[dateString];
    let format = self.numberFormat.find(
      ({ numberFormatType }) => numberFormatType === "CALENDAR"
    );
    if (format) {
      const options =
        format.numberMultiplier == 0
          ? {}
          : { maximumFractionDigits: format.maximumFractionDigits };
      const formatter = new Intl.NumberFormat(format.numberFormat, options);
      if (format.numberMultiplier != "") {
        price = formatter.format(Math.round(price) / format.numberMultiplier);
      } else {
        price = formatter.format(Math.round(price));
      }
    } else if (self.numberFormat.length > 0) {
      format = self.numberFormat[0];
      if (format.numberMultiplier != "") {
        price = Math.round(price) / format.numberMultiplier[0];
      }
    } else {
      price = Math.round(price);
    }
    return price;
  }

  countDigits(number) {
    return number.toString().replace(".", "").replace("-", "").length;
  }

  showPriceToggleSection() {
    const { currency, smartCalendar, numberFormat } = this;
    if (smartCalendar) {
      let format = numberFormat.find(
        ({ numberFormatType }) => numberFormatType === "CALENDAR"
      );
      let unit = "";
      if (format) {
        if (format.numberMultiplier === "100") {
          unit = "hundered";
        } else if (format.numberMultiplier === "1000") {
          unit = "thousand";
        }
      }
      const priceText = i18n.t("calendar.allPricesIn", { unit, currency });
      const showPricesLabel = i18n.t("calendar.showPrices");
      const priceControl = $(
        `<div class="datepicker-price-control">
          <div class="d-flex justify-content-end">
            <div class="text-right" style="margin-right: 35px !important;">
              <div style="line-height: 13px; font-size: 14px;"><b>${showPricesLabel}</b></div>
              <span class="price-currency mt-0">( ${priceText} )</span>
            </div>
            <div class="custom-control custom-switch">
              <input type="checkbox" ${
                this.showCalendarPrice ? "checked" : ""
              } class="custom-control-input" id="priceControlSwitch">
              <label class="custom-control-label" for="priceControlSwitch"></label>
            </div>
          </div>
        </div>`
      );

      $("#ui-datepicker-div").append(priceControl);
      $("#priceControlSwitch").on("change", () => {
        this.showCalendarPrice = !this.showCalendarPrice;
        this.setPrices(this.showCalendarPrice);
      });
      this.setPrices(this.showCalendarPrice);
    }
  }

  formatDate(dateString) {
    const date = new Date(dateString);
    const formattedDate = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
    return formattedDate;
  }

  isCalendarOpen() {
    return $("#ui-datepicker-div").is(":visible");
  }

  isInitialized() {
    return Boolean($(this.fromSelector).data("datepicker"));
  }

  refresh() {
    if (!this.isInitialized()) {
      return;
    }

    $(this.fromSelector).datepicker("refresh");
    $(this.toSelector).datepicker("refresh");

    if (this.isCalendarOpen()) {
      this.setPrices(this.showCalendarPrice);
      if (this.presetHighlight) {
        this.presetDateHighlights();
      }
    }
  }

  setPriceData(data) {
    this.priceData = data;
    this.refresh();
  }

  setMinLosData(data) {
    this.minLos = data;
    const day = moment(this.options.defaultFromDate).format("YYYY-MM-DD");
    const los = data[day] || 1;
    if (los > 1 && !this.options.defaultToDate) {
      const toDate = moment(this.options.defaultFromDate).add(los, "days");
      this.options.defaultToDate = toDate.toDate();
      this.toDateObj = null;
      $(this.toSelector).val(toDate.format("DD MMM YYYY"));
      this.presetDateHighlights();
    }
    this.refresh();
  }

  show() {
    const scrollTop =
      window.pageYOffset || document.documentElement.scrollTop;

    $(this.fromSelector).datepicker("show");
    window.scrollTo(0, scrollTop);
  }

  getCellDateInstance($cell) {
    const dataMonth = parseInt($cell.attr("data-month"), 10);
    const dataYear = parseInt($cell.attr("data-year"), 10);
    const dataDate = parseInt($cell.find("a").attr("data-date"), 10);

    if (!isNaN(dataMonth) && !isNaN(dataYear) && !isNaN(dataDate)) {
      return new Date(dataYear, dataMonth, dataDate);
    }

    return null;
  }

  drawArrow(input) {
    const $input = $(input);
    const widget = $input.datepicker("widget");

    widget.addClass("direction-up");

    var arrow = widget.find(".datepicker-arrow");

    if (!arrow.length) {
      arrow = $('<div class="datepicker-arrow"/>')
        .css({
          width: 0,
          height: 0,
          borderStyle: "solid",
          margin: "0 10px",
          position: "absolute",
        })
        .appendTo(widget);
    }

    arrow.css({
      left: "20%",
      borderWidth: "0 19px 10px 19px",
      top: "-7px",
      bottom: "auto",
    });
  }

  addCloseButton() {
    const self = this;
    const $closeCal = $("<span>", { class: "close-cal" });

    const $closeLinks = $("<a>", { class: "close-links" });

    $closeLinks.on("click", function () {
      $(`${self.fromSelector}, ${self.toSelector}`).datepicker("hide");
    });

    $closeLinks.append($("<div>"), $("<div>"));
    $closeCal.append($closeLinks);

    $("#ui-datepicker-div").append($closeCal);
  }

  calendarVerticalOffset(input) {
    const datepickerContainer = $("#ui-datepicker-div");

    const inputOffset = $(input).offset();
    const inputHeight = $(input).outerHeight();

    datepickerContainer.css({
      top: inputOffset.top + inputHeight + "px",
    });
  }

  presetDateHighlights() {
    const self = this;
    const { defaultFromDate, defaultToDate } = self.options;

    if (!self.fromDateObj && defaultFromDate) {
      self.fromDateObj = new Date(defaultFromDate);
      self.fromDate = self.formatDate(self.fromDateObj);
    }

    if (!self.toDateObj && defaultToDate) {
      self.toDateObj = new Date(defaultToDate);
      self.toDate = self.formatDate(self.toDateObj);
    }

    if (self.fromDateObj && self.toDateObj) {
      $("#ui-datepicker-div td").each(function () {
        $(this).removeClass("from-highlight to-highlight range-highlight");
      });
      $("#ui-datepicker-div td").each(function () {
        const cellDate = self.getCellDateInstance($(this));
        if (cellDate && cellDate.getTime() === self.fromDateObj.getTime()) {
          $(this).addClass("from-highlight");
          $(this).removeClass("range-highlight");
        }

        if (
          cellDate &&
          cellDate > self.fromDateObj &&
          cellDate < self.toDateObj
        ) {
          $(this).addClass("range-highlight");
        }

        if (cellDate && cellDate.getTime() === self.toDateObj.getTime()) {
          $(this).addClass("to-highlight");
          $(this).removeClass("range-highlight");
        }
      });
    }
  }

  prepareDatepickerDisplay(instance) {
    setTimeout(() => {
      this.showPriceToggleSection();
      this.drawArrow(instance);
      this.addCloseButton();
      this.calendarVerticalOffset(instance);
    }, 10);
  }

  init(fromSelector, toSelector) {
    const self = this;
    const regional = this.regional || {};

    $(fromSelector).datepicker({
      ...regional,
      firstDay: 1,
      numberOfMonths: 2,
      dateFormat: self.format,
      minDate: "+0D",
      maxDate: "+1Y",
      beforeShowDay: this.highlightDates.bind(this),
      beforeShow: function () {
        self.prepareDatepickerDisplay(this);
        if (self.presetHighlight) {
          setTimeout(() => self.presetDateHighlights(), 100);
        }
      },
      onSelect: (selectedDate) => {
        this.presetHighlight = false;
        this.fromDate = selectedDate;
        // Read the picked date from jQuery UI's internal state — a proper JS Date — instead
        // of `new Date(selectedDate)` which can't parse localised month names like "März".
        const picked = $(fromSelector).datepicker("getDate") || new Date();
        this.fromDateObj = picked;
        this.fromDateObj.setHours(0, 0, 0, 0);
        $(fromSelector).val(selectedDate);

        const formattedDate = moment(this.fromDateObj).format("YYYY-MM-DD");
        const dateMinLos = this.minLos[formattedDate] ?? 1;

        let minDate = new Date(this.fromDateObj);
        minDate.setDate(minDate.getDate() + dateMinLos);
        if (this.toDateObj && this.fromDateObj > this.toDateObj) {
          this.toDateObj = minDate;
        }
        $(toSelector)
          .datepicker("option", "minDate", minDate)
          .datepicker("refresh");
        $(fromSelector).datepicker("refresh");
        setTimeout(() => $(toSelector).focus(), 100);
      },
      onChangeMonthYear: () => {
        self.prepareDatepickerDisplay(this);
      },
    });

    $(toSelector).datepicker({
      ...regional,
      firstDay: 1,
      numberOfMonths: 2,
      dateFormat: self.format,
      minDate: "+0D",
      maxDate: "+1Y",
      beforeShowDay: this.highlightDates.bind(this),
      beforeShow: function () {
        self.prepareDatepickerDisplay(this);

        if (self.presetHighlight) {
          setTimeout(() => self.presetDateHighlights(), 100);
        }

        if (!self.fromDate) {
          setTimeout(() => $(fromSelector).datepicker("show"), 10);
          return false;
        }
      },
      onSelect: (selectedDate) => {
        this.presetHighlight = false;
        this.toDate = selectedDate;
        // Read the picked date from jQuery UI's internal state — a proper JS Date — instead
        // of `new Date(selectedDate)` which can't parse localised month names like "März".
        const picked = $(toSelector).datepicker("getDate") || new Date();
        this.toDateObj = picked;
        this.toDateObj.setHours(0, 0, 0, 0);
        $(toSelector).val(selectedDate);
        $(`${fromSelector}, ${toSelector}`).datepicker("refresh");
        $(toSelector).blur();
      },
      onChangeMonthYear: () => {
        self.prepareDatepickerDisplay(this);
      },
    });

    $(document).on("mouseenter", "#ui-datepicker-div td", (event) => {
      if (!this.fromDateObj) return;

      const parsedDate = self.getCellDateInstance($(event.currentTarget));
      if (parsedDate) {
        $("#ui-datepicker-div td").each(function () {
          const cellDate = self.getCellDateInstance($(this));
          if (
            cellDate &&
            cellDate > self.fromDateObj &&
            cellDate < parsedDate
          ) {
            $(this).addClass("range-highlight");
          } else {
            $(this).removeClass("range-highlight");
          }
        });
      }
    });

    $(document).on("mouseleave", "#ui-datepicker-div td", () => {
      $("#ui-datepicker-div td").removeClass("range-highlight");
      $("#ui-datepicker-div td").each(function () {
        if (self.fromDateObj && self.toDateObj) {
          const cellDate = self.getCellDateInstance($(this));
          if (
            cellDate &&
            cellDate > self.fromDateObj &&
            cellDate < self.toDateObj
          ) {
            $(this).addClass("range-highlight");
          } else {
            $(this).removeClass("range-highlight");
          }
        } else {
          $(this).removeClass("range-highlight");
        }
      });
    });

    if (!this.smartCalendar) {
      setTimeout(() => $("#ui-datepicker-div").addClass("not-smart"), 1000);
    } else {
      setTimeout(() => $("#ui-datepicker-div").removeClass("not-smart"), 1000);
    }

  }
}

export default DateRangePicker;
